import React, { useState, useEffect } from 'react';

const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";

/**
 * Agent Outbox - Shows pending actions awaiting approval
 * Allows user to review, approve, reject, or edit queued emails/messages
 */
function AgentOutbox({ isOpen, onClose }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActions, setSelectedActions] = useState(new Set());
  const [pendingActionIds, setPendingActionIds] = useState(new Set());
  const [filter, setFilter] = useState('pending'); // pending | approved | all

  useEffect(() => {
    if (isOpen) {
      fetchActions();
    }
  }, [isOpen, filter]);

  const fetchActions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('bd_auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const endpoint = filter === 'pending'
        ? '/api/agent/outbox'
        : `/api/agent/actions?status=${filter === 'all' ? '' : filter}`;

      const res = await fetch(`${BASE_URL}${endpoint}`, { headers });
      const data = await res.json();
      setActions(data.actions || []);
    } catch (err) {
      console.error('Failed to fetch actions:', err);
    } finally {
      setLoading(false);
    }
  };

  const markPending = (actionId, on) => {
    setPendingActionIds(prev => {
      const next = new Set(prev);
      if (on) next.add(actionId); else next.delete(actionId);
      return next;
    });
  };

  const handleApprove = async (actionId) => {
    if (pendingActionIds.has(actionId)) return; // ignore double-clicks
    markPending(actionId, true);
    try {
      const token = localStorage.getItem('bd_auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const res = await fetch(`${BASE_URL}/api/agent/actions/${actionId}/approve`, {
        method: 'POST',
        headers
      });

      if (res.ok) {
        fetchActions(); // Refresh list
      } else {
        alert(`Could not approve action (HTTP ${res.status}).`);
      }
    } catch (err) {
      console.error('Failed to approve action:', err);
      alert('Could not approve — check your connection.');
    } finally {
      markPending(actionId, false);
    }
  };

  const handleReject = async (actionId, reason) => {
    if (pendingActionIds.has(actionId)) return;
    markPending(actionId, true);
    try {
      const token = localStorage.getItem('bd_auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const res = await fetch(`${BASE_URL}/api/agent/actions/${actionId}/reject`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason })
      });

      if (res.ok) {
        fetchActions();
      } else {
        alert(`Could not reject action (HTTP ${res.status}).`);
      }
    } catch (err) {
      console.error('Failed to reject action:', err);
      alert('Could not reject — check your connection.');
    } finally {
      markPending(actionId, false);
    }
  };

  const handleBulkApprove = async () => {
    try {
      const token = localStorage.getItem('bd_auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const res = await fetch(`${BASE_URL}/api/agent/actions/approve-multiple`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ actionIds: Array.from(selectedActions) })
      });

      if (res.ok) {
        setSelectedActions(new Set());
        fetchActions();
      }
    } catch (err) {
      console.error('Failed to bulk approve:', err);
    }
  };

  const toggleSelection = (actionId) => {
    const newSelected = new Set(selectedActions);
    if (newSelected.has(actionId)) {
      newSelected.delete(actionId);
    } else {
      newSelected.add(actionId);
    }
    setSelectedActions(newSelected);
  };

  const selectAll = () => {
    if (selectedActions.size === actions.length) {
      setSelectedActions(new Set());
    } else {
      setSelectedActions(new Set(actions.map(a => a.id)));
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.title}>
            <span style={styles.titleIcon}>📬</span>
            Agent Outbox
            {actions.length > 0 && (
              <span style={styles.badge}>{actions.length}</span>
            )}
          </div>
          <button style={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Filter Tabs */}
        <div style={styles.filterTabs}>
          <button
            style={{...styles.filterTab, ...(filter === 'pending' ? styles.filterTabActive : {})}}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            style={{...styles.filterTab, ...(filter === 'approved' ? styles.filterTabActive : {})}}
            onClick={() => setFilter('approved')}
          >
            Approved
          </button>
          <button
            style={{...styles.filterTab, ...(filter === 'all' ? styles.filterTabActive : {})}}
            onClick={() => setFilter('all')}
          >
            All
          </button>
        </div>

        {/* Bulk Actions */}
        {filter === 'pending' && actions.length > 0 && (
          <div style={styles.bulkActions}>
            <label style={styles.selectAllLabel}>
              <input
                type="checkbox"
                checked={selectedActions.size === actions.length}
                onChange={selectAll}
              />
              <span>Select All</span>
            </label>
            {selectedActions.size > 0 && (
              <>
                <button style={styles.bulkBtn} onClick={handleBulkApprove}>
                  Approve Selected ({selectedActions.size})
                </button>
                <button style={styles.bulkBtnSecondary} onClick={() => setSelectedActions(new Set())}>
                  Clear
                </button>
              </>
            )}
          </div>
        )}

        {/* Action List */}
        <div style={styles.actionList}>
          {loading ? (
            <div style={styles.emptyState}>Loading actions...</div>
          ) : actions.length === 0 ? (
            <div style={styles.emptyState}>
              {filter === 'pending'
                ? '✅ No pending actions. Your Agent will queue messages here for review.'
                : `No ${filter} actions found.`}
            </div>
          ) : (
            actions.map((action) => (
              <ActionCard
                key={action.id}
                action={action}
                isSelected={selectedActions.has(action.id)}
                onToggleSelect={() => toggleSelection(action.id)}
                onApprove={() => handleApprove(action.id)}
                onReject={() => handleReject(action.id, 'User rejected')}
                showCheckbox={filter === 'pending'}
                pending={pendingActionIds.has(action.id)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ActionCard({ action, isSelected, onToggleSelect, onApprove, onReject, showCheckbox, pending = false }) {
  const [expanded, setExpanded] = useState(false);

  const priorityColors = {
    high: '#ff5a5a',
    medium: '#ffc832',
    low: '#888'
  };

  const statusColors = {
    pending: '#ffc832',
    approved: '#2196f3',
    rejected: '#ff5a5a',
    sent: '#2196f3',
    failed: '#ff5a5a'
  };

  const typeIcons = {
    email: '📨',
    linkedin: '💼',
    research: '🔍',
    calendar: '📅'
  };

  return (
    <div style={styles.actionCard}>
      <div style={styles.actionHeader}>
        {showCheckbox && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            style={styles.checkbox}
          />
        )}
        <div style={styles.actionMeta}>
          <span style={styles.actionType}>
            {typeIcons[action.type]} {action.type.toUpperCase()}
          </span>
          <span style={{...styles.actionPriority, backgroundColor: priorityColors[action.priority]}}>
            {action.priority}
          </span>
          <span style={{...styles.actionStatus, backgroundColor: statusColors[action.status]}}>
            {action.status}
          </span>
        </div>
        <button style={styles.expandBtn} onClick={() => setExpanded(!expanded)}>
          {expanded ? '▼' : '▶'}
        </button>
      </div>

      <div style={styles.actionTarget}>{action.target}</div>
      <div style={styles.actionSubject}>{action.subject}</div>

      {action.opportunityTitle && (
        <div style={styles.actionOpp}>
          Re: {action.opportunityTitle.substring(0, 80)}...
        </div>
      )}

      {expanded && (
        <div style={styles.actionBody}>
          <div style={styles.bodyLabel}>Message:</div>
          <pre style={styles.bodyText}>{action.body}</pre>

          <div style={styles.bodyMeta}>
            <div>Scheduled: {new Date(action.scheduledFor).toLocaleString()}</div>
            <div>Created: {new Date(action.createdAt).toLocaleString()}</div>
          </div>
        </div>
      )}

      {action.status === 'pending' && (
        <div style={styles.actionButtons}>
          <button
            style={{ ...styles.approveBtn, opacity: pending ? 0.6 : 1, cursor: pending ? 'wait' : 'pointer' }}
            onClick={onApprove}
            disabled={pending}
          >
            {pending ? '… Sending' : '✓ Approve & Send'}
          </button>
          <button
            style={{ ...styles.rejectBtn, opacity: pending ? 0.6 : 1, cursor: pending ? 'wait' : 'pointer' }}
            onClick={onReject}
            disabled={pending}
          >
            ✗ Reject
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  drawer: {
    width: '600px',
    maxWidth: '90vw',
    backgroundColor: 'var(--bg-1)',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
  },
  header: {
    padding: '20px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-0)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  titleIcon: {
    fontSize: '24px',
  },
  badge: {
    backgroundColor: 'var(--accent)',
    color: 'var(--bg-1)',
    fontSize: '12px',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '12px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: 'var(--text-2)',
    cursor: 'pointer',
    padding: '0',
    lineHeight: '1',
  },
  filterTabs: {
    display: 'flex',
    padding: '0 20px',
    borderBottom: '1px solid var(--border)',
  },
  filterTab: {
    padding: '12px 20px',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: 'var(--text-2)',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterTabActive: {
    color: 'var(--accent)',
    borderBottomColor: 'var(--accent)',
  },
  bulkActions: {
    padding: '12px 20px',
    backgroundColor: 'var(--bg-2)',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectAllLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: 'var(--text-1)',
    cursor: 'pointer',
  },
  bulkBtn: {
    padding: '6px 12px',
    backgroundColor: 'var(--accent)',
    color: 'var(--bg-1)',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  bulkBtnSecondary: {
    padding: '6px 12px',
    backgroundColor: 'var(--bg-3)',
    color: 'var(--text-1)',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  actionList: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
  },
  emptyState: {
    textAlign: 'center',
    color: 'var(--text-2)',
    fontSize: '14px',
    padding: '40px 20px',
  },
  actionCard: {
    backgroundColor: 'var(--bg-2)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
  },
  actionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px',
  },
  checkbox: {
    cursor: 'pointer',
  },
  actionMeta: {
    display: 'flex',
    gap: '8px',
    flex: 1,
  },
  actionType: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--text-0)',
    fontFamily: 'var(--font-mono)',
  },
  actionPriority: {
    fontSize: '9px',
    padding: '2px 6px',
    borderRadius: '3px',
    color: '#fff',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  actionStatus: {
    fontSize: '9px',
    padding: '2px 6px',
    borderRadius: '3px',
    color: '#fff',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  expandBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-2)',
    cursor: 'pointer',
    fontSize: '12px',
  },
  actionTarget: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-0)',
    marginBottom: '4px',
  },
  actionSubject: {
    fontSize: '13px',
    color: 'var(--text-1)',
    marginBottom: '8px',
  },
  actionOpp: {
    fontSize: '11px',
    color: 'var(--text-2)',
    fontStyle: 'italic',
    marginBottom: '12px',
  },
  actionBody: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid var(--border)',
  },
  bodyLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--text-2)',
    marginBottom: '6px',
    textTransform: 'uppercase',
  },
  bodyText: {
    fontSize: '12px',
    color: 'var(--text-1)',
    lineHeight: '1.6',
    backgroundColor: 'var(--bg-3)',
    padding: '12px',
    borderRadius: '4px',
    whiteSpace: 'pre-wrap',
    fontFamily: 'var(--font-mono)',
    marginBottom: '12px',
  },
  bodyMeta: {
    fontSize: '10px',
    color: 'var(--text-2)',
    display: 'flex',
    gap: '12px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
  },
  approveBtn: {
    flex: 1,
    padding: '8px 12px',
    backgroundColor: 'var(--accent)',
    color: 'var(--bg-1)',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  rejectBtn: {
    padding: '8px 12px',
    backgroundColor: 'transparent',
    color: 'var(--red)',
    border: '1px solid var(--red)',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default AgentOutbox;
